import cn from 'classnames'
import * as React from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'

import SocialMediaLinks from '../../components/social-media'

import styles from './footer.module.scss'

interface IFooterSection {
  last?: boolean
  title: React.ReactNode
  href?: string
  links: Array<{
    title: React.ReactNode
    href: string
    external?: boolean
  }>
  extra?: React.ReactNode
}

const FooterSection = ({ title, href, links, extra, last = false }: IFooterSection) => (
  <div className={cn(styles.section, last && styles.sectionLast)}>
    <h6 className={styles.titleLink}>{href ? <a href={href}>{title}</a> : title}</h6>
    {links.map((l) => (
      <a
        className={styles.footerLink}
        href={l.href}
        key={l.href}
        rel="noopener noreferrer"
        target={l.external ? '_blank' : '_self'}
      >
        {l.title}
      </a>
    ))}
    {extra}
  </div>
)

interface IFooterProps {
  inverted?: boolean
}

const Footer = ({ inverted }: IFooterProps) => {
  const sections = [
    {
      links: [
        {
          href: '/#signup',
          title: <FormattedMessage defaultMessage="Skapa ett konto gratis" id="component.footer.signup" />,
        },
        {
          href: '/bokforing',
          title: <FormattedMessage defaultMessage="Gratis bokföringsprogram" id="component.footer.accounting" />,
        },
        {
          href: '/fakturering',
          title: <FormattedMessage defaultMessage="Gratis faktureringstjänst" id="component.footer.invoicing" />,
        },
        {
          href: '/lon',
          title: <FormattedMessage defaultMessage="Gratis lönehanteringsverktyg" id="component.footer.salaries" />,
        },
      ],
      title: <FormattedMessage defaultMessage="Dooer gratisprogram" id="component.footer.freeware" />,
      href: '/',
    },
    {
      links: [
        {
          href: '/karriar',
          title: <FormattedMessage defaultMessage="Karriär" id="component.footer.career" />,
        },
        {
          href: '/karriar#jobs',
          title: <FormattedMessage defaultMessage="Lediga tjänster" id="component.footer.career-open-positions" />,
        },
        {
          href: '/press',
          title: <FormattedMessage defaultMessage="Press" id="component.footer.press" />,
        },
      ],
      title: <FormattedMessage defaultMessage="Om oss" id="component.footer.about" />,
      href: '/om-oss',
    },
    {
      links: [
        {
          href: '/hjalp',
          title: <FormattedMessage defaultMessage="Hjälp & Vanliga Frågor" id="component.footer.help-and-faq" />,
        },
        {
          href: 'https://www.facebook.com/groups/dooer',
          title: (
            <FormattedMessage defaultMessage="Facebookgrupp - Dooer Community" id="component.footer.facebook-group" />
          ),
          external: true,
        },
        {
          href: 'mailto:support@dooer.com',
          title: <FormattedMessage defaultMessage="support@dooer.com" id="component.footer.support-email" />,
          external: true,
        },
      ],
      title: <FormattedMessage defaultMessage="Support" id="component.footer.support" />,
    },
    {
      links: [
        {
          href: 'mailto:hej@dooer.com',
          external: true,
          title: (
            <span className={styles.brandLink}>
              <FormattedMessage defaultMessage="hej@dooer.com" id="component.footer.email" />
            </span>
          ),
        },
      ],
      title: <FormattedMessage defaultMessage="Kontakta oss" id="component.footer.contact" />,
      extra: (
        <>
          <h6 className={cn(styles.titleLink, styles.marginTop)}>{'Dooer AB'}</h6>
          <span className={cn(styles.footerLink, 'nowrap')}>{'Kopparbergsvägen 8'}</span>
          <span className={styles.footerLink}>{'722 13, Västerås'}</span>
        </>
      ),
    },
  ]

  return (
    <>
      <footer className={cn(styles.root, inverted && styles.inverted)}>
        <div className={styles.inner}>
          <div className={styles.sections}>
            {sections.map(({ href, title, links, extra }, i, arr) => (
              <FooterSection
                extra={extra}
                href={href}
                key={href}
                last={i === arr.length - 1}
                links={links}
                title={title}
              />
            ))}
          </div>
          <div className={styles.footerFooter}>
            <span className={styles.footerFooterLinks}>
              <a className={styles.footerLink} href="/tos">
                <FormattedMessage defaultMessage="Allmänna villkor" id="component.footer.tos" />
              </a>
              <a className={styles.footerLink} href="/gdpr">
                <FormattedMessage defaultMessage="Integritetspolicy" id="component.footer.integrity-policy" />
              </a>
              <a className={styles.footerLink} href="/cookies">
                <FormattedMessage defaultMessage="Cookie policy" id="component.footer.cookie-notice" />
              </a>
            </span>
            <SocialMediaLinks />
            <span className={styles.footerFooterCopyright}>
              <FormattedMessage
                defaultMessage="© Dooer AB {year}"
                id="component.footer.copyright"
                values={{
                  year: <FormattedDate value={new Date()} year="numeric" />,
                }}
              />
            </span>
          </div>
        </div>
      </footer>
    </>
  )
}

export default Footer
